/**
 * Shared types and constants for the properties API.
 *
 * This is the single source of truth for the request/response contract
 * between the API (`packages/runtimes/api`) and its clients
 * (`packages/runtimes/web`).
 */

import type { PropertyChatRefDTO } from "./chat"

export const PROPERTY_TYPES = ["apartment", "house", "office", "other"] as const

export type PropertyType = (typeof PROPERTY_TYPES)[number]

/**
 * A property as returned by the API (JSON-serialized — Dates are ISO strings).
 */
export interface PropertyDTO {
	id: string
	name: string
	type: PropertyType
	customType: string | null
	/** Full Markdown description (the versioned field, ZPL-186). */
	description: string | null
	/** AI-generated plain-text one-sentence summary (non-MD). */
	descriptionOneliner: string | null
	/** AI-generated plain-text short summary, ≤3 sentences (non-MD). */
	descriptionShort: string | null
	/** Owner-selected cover image URL. Null means clients render the type placeholder. */
	coverPhotoUrl: string | null
	createdAt: string
	updatedAt: string
	/**
	 * Present on `GET /properties/:id`; absent on `GET /properties`.
	 * Two entries when the user has membership: one `private`, one `shared`.
	 */
	chats?: PropertyChatRefDTO[]
	/**
	 * Lightweight roll-up for `GET /properties`, used by clients to signal
	 * cross-property chat activity without shipping full thread refs in the list
	 * response.
	 */
	chatUnreadTotal?: number
}

/**
 * Request body for `POST /api/properties`.
 *
 * Server-side validation (see API routes) enforces:
 * - name trimmed, 1..PROPERTY_LIMITS.nameMax chars
 * - customType required (1..PROPERTY_LIMITS.customTypeMax) iff type === "other"
 * - description optional, up to PROPERTY_LIMITS.descriptionMax chars
 */
export interface CreatePropertyBody {
	id?: string
	name: string
	type: PropertyType
	customType?: string
	description?: string
}

export interface PropertyResponse {
	property: PropertyDTO
}

export interface PropertyListResponse {
	properties: PropertyDTO[]
}

export interface ErrorResponse {
	message: string
}

export const PROPERTY_LIMITS = {
	nameMax: 120,
	customTypeMax: 60,
	descriptionMax: 2000,
} as const

/**
 * Request body for `PATCH /api/common/properties/:id`. At least one
 * field required (Joi `.min(1)` server-side). Empty `description` after
 * trim is stored as `null`.
 */
export interface UpdatePropertyBody {
	name?: string
	description?: string
	coverPhotoUrl?: string | null
	/**
	 * When updating the description, whether the AI should rewrite the full
	 * Markdown (ZPL-186). Ignored unless `description` is present.
	 */
	improve?: boolean
}

/**
 * Request body for `POST /api/common/properties/:id/transfer-ownership`.
 * `confirmationName` must equal the current property name (trimmed) —
 * server validates as defense-in-depth on top of the client gate.
 * `newOwnerId` must be a user who currently has a membership on the
 * property; that membership row is deleted as part of the same DB
 * transaction that flips ownership.
 */
export interface TransferOwnershipBody {
	newOwnerId: string
	confirmationName: string
}
